// SU data loader — v0M2.1 placeholder.
//
// Per §6.1 #7 (hybrid balance data: xlsx → JSON pipeline) and MAP.md
// §4 (map data format): data loads as pure JSON at runtime. Every
// schema carries a schemaVersion; loaders reject unknown versions
// deliberately (SG-pattern discipline against silent drift).
//
// v0M2.2 wires the xlsx → JSON build pipeline and per-schema loaders
// (dials, units, terrain, tech, factions, maps).

/**
 * Legacy shared schema constant — kept as `1` so pre-v0M1.9.16 consumers
 * (specifically the map-loader / karth-sample.json path at
 * `src/client/map/map-loader.ts`) keep working without a coordinated map
 * regeneration.
 *
 * **v0M1.9.16 amendment (2026-07-12):** per-schema version constants
 * below split what was one shared version into independent, per-schema
 * versions. Different schemas evolve independently — a dials-sheet bump
 * shouldn't force a map regeneration and vice versa. New consumers
 * MUST import their per-schema constant (`BALANCE_SCHEMA_VERSION`,
 * `MAP_SCHEMA_VERSION`, ...) rather than this shared one.
 */
export const SCHEMA_VERSION = 1;

/** Schema version emitted by `scripts/convert-xlsx.ts` into `dials.json`.
 *  Bump on breaking changes to the dials/structures/cohorts shape. */
export const BALANCE_SCHEMA_VERSION = 2;

/** Schema version consumed by `map-loader.ts` for `karth-sample.json` /
 *  future map JSON blobs. Bump on breaking map-blob shape changes. */
export const MAP_SCHEMA_VERSION = 1;

/** v0M4.3.1 (2026-07-23) — Schema version emitted by scripts/convert-xlsx.ts
 *  into battle-map.json from Michael's BattleMap.xlsx. Bump on breaking
 *  changes to terrainTemplates / formationTemplates shape.
 *  v0M13.2a (2026-07-30) — bumped 1 → 2: river-category terrainTemplates
 *  are now 20 cols × 10 rows (half-battlescape) instead of 20×20; each
 *  authored xlsx sheet is auto-split into `{name}_top` + `{name}_bot`
 *  candidates at ingestion. Siege categories retain 20×20. */
export const BATTLE_MAP_SCHEMA_VERSION = 2;

/** v0M9.1 (2026-07-24) — Schema version emitted by scripts/convert-xlsx.ts
 *  into knowledge.json from Michael's KnowledgeTree.xlsx (both sheets:
 *  KnowledgeUnlocks + CommanderUnlocks). Bump on breaking knowledge /
 *  commanderLevels shape. */
export const KNOWLEDGE_SCHEMA_VERSION = 1;

export class SchemaVersionError extends Error {
  constructor(
    public readonly expected: number,
    public readonly actual: unknown,
    public readonly path: string,
  ) {
    super(
      `Schema version mismatch loading ${path}: expected ${expected}, got ${String(actual)}`,
    );
    this.name = 'SchemaVersionError';
  }
}

/**
 * Assert the loaded blob carries the expected schemaVersion. Throws
 * SchemaVersionError on mismatch. Per SG pattern — no silent version
 * drift.
 */
export function assertSchemaVersion(
  blob: unknown,
  expected: number,
  path: string,
): asserts blob is { schemaVersion: number } {
  if (
    typeof blob !== 'object' ||
    blob === null ||
    !('schemaVersion' in blob) ||
    (blob as { schemaVersion: unknown }).schemaVersion !== expected
  ) {
    throw new SchemaVersionError(
      expected,
      (blob as { schemaVersion?: unknown } | null)?.schemaVersion,
      path,
    );
  }
}
